import React, {useEffect} from "react";
import {matchPath, Redirect, Route, Switch, useHistory, useLocation, useParams} from "react-router-dom";
import {routeFunctions, routes} from "../../../../../core/models/static/routes/app-routes";
import useAccessLevels from "../../../../hooks/use-access-levels";
import PurchaseOrdersViews from "./orders";
import PurchaseHistoryViews from "./history";
import PurchasePlanningListView from "./planning-list";
import PurchasePlanningDetailsView from "./planning-details";

/**
 * Dual-scope purchase shell: system (/purchase/*) + vendor (/vendor/:id/purchases/*).
 * Mirrors common/orders dual customer/system mount.
 *
 * Planning routes are always registered (like orders) so a brief access-flag
 * flicker cannot drop Switch through to the catch-all Redirect mid-navigation.
 */
const PurchaseViews = () => {
    const history = useHistory();
    const location = useLocation();
    const params = useParams();
    const operatorAccessLevels = useAccessLevels();
    const canAccessPlanning = operatorAccessLevels.packages.purchasing.viewPurchasePlanning;

    /**
     * Base path redirects for system purchase and vendor purchases root.
     */
    useEffect(() => {
        if (matchPath(location.pathname, {path: routes.purchase.base, exact: true})) {
            if (operatorAccessLevels.packages.purchasing.viewPurchasePlanning) {
                history.replace(routes.purchase.planningList);
            } else if (operatorAccessLevels.packages.purchasing.viewPurchaseOrders) {
                history.replace(routes.purchase.orders);
            } else if (operatorAccessLevels.packages.purchasing.viewPurchaseHistory) {
                history.replace(routes.purchase.history);
            } else {
                history.replace(routes.purchase.base.concat(routes.error.accessDeniedForSection));
            }
            return;
        }
        if (matchPath(location.pathname, {path: routes.purchase.list, exact: true})) {
            history.replace(routes.purchase.orders);
            return;
        }
        if (matchPath(location.pathname, {path: routes.vendor.purchases.base, exact: true}) && params.vendorId) {
            if (operatorAccessLevels.packages.purchasing.viewPurchaseOrders) {
                history.replace(routeFunctions.vendor.purchases.orders.base(params.vendorId));
            } else if (operatorAccessLevels.packages.purchasing.viewPurchaseHistory) {
                history.replace(routeFunctions.vendor.purchases.history.base(params.vendorId));
            } else {
                history.replace(
                    routeFunctions.vendor.purchases.base(params.vendorId)
                        .concat(routes.error.accessDeniedForSection),
                );
            }
        }
    }, [
        history,
        location.pathname,
        operatorAccessLevels.packages.purchasing.viewPurchasePlanning,
        operatorAccessLevels.packages.purchasing.viewPurchaseOrders,
        operatorAccessLevels.packages.purchasing.viewPurchaseHistory,
        params.vendorId,
    ]);

    return (
        <Switch>
            <Route path={routes.purchase.base} exact/>
            <Route path={routes.purchase.list} exact/>
            <Route path={routes.vendor.purchases.base} exact/>
            <Route path={[
                routes.purchase.orders,
                routes.purchase.list,
                routes.vendor.purchases.orders.base,
                routes.vendor.purchases.orders.single,
            ]}>
                <PurchaseOrdersViews/>
            </Route>
            <Route path={[
                routes.purchase.history,
                routes.vendor.purchases.history.base,
                routes.vendor.purchases.history.single,
            ]}>
                <PurchaseHistoryViews/>
            </Route>
            <Route path={routes.purchase.planningList} exact>
                {
                    canAccessPlanning
                        ? <PurchasePlanningListView/>
                        : <Redirect to={routes.purchase.base.concat(routes.error.accessDeniedForSection)}/>
                }
            </Route>
            <Route path={routes.purchase.planningDetails} exact>
                {
                    canAccessPlanning
                        ? <PurchasePlanningDetailsView/>
                        : <Redirect to={routes.purchase.base.concat(routes.error.accessDeniedForSection)}/>
                }
            </Route>
            <Redirect to={routes.purchase.base.concat(routes.error.accessDeniedForSection)}/>
        </Switch>
    );
};

export default PurchaseViews;
